// tauriFileSave.js
// Helper para guardar archivos (PDF, Excel, binarios) usando las APIs de Tauri.
// Usa @tauri-apps/api (dialog + fs) para abrir diálogo nativo y escribir en disco.

import { save } from '@tauri-apps/plugin-dialog';
import { writeFile } from '@tauri-apps/plugin-fs';

/**
 * Guarda un ArrayBuffer en disco usando el diálogo nativo de Tauri.
 * @param {ArrayBuffer} arrayBuffer - Los datos a guardar
 * @param {string} defaultFileName - Nombre sugerido para el archivo
 * @param {Object} options - Opciones adicionales
 * @param {string} options.title - Título del diálogo
 * @param {Array} options.filters - Filtros de tipo de archivo, ej: [{ name: 'PDF', extensions: ['pdf'] }]
 * @returns {Promise<string|null>} - Ruta del archivo guardado o null si se canceló
 */
export async function saveArrayBufferToDisk(arrayBuffer, defaultFileName, options = {}) {
  try {
    // Abrir diálogo de guardado nativo
    const filePath = await save({
      defaultPath: defaultFileName,
      title: options.title || 'Guardar archivo',
      filters: options.filters || [],
    });

    // Si el usuario canceló, retornar null
    if (!filePath) {
      return null;
    }

    // Convertir ArrayBuffer a Uint8Array para escribir
    const uint8Array = new Uint8Array(arrayBuffer);

    // Escribir el archivo en disco
    await writeFile(filePath, uint8Array);

    return filePath;
  } catch (error) {
    console.error('Error al guardar archivo con Tauri:', error);
    throw error;
  }
}

/**
 * Guarda un Blob en disco usando el diálogo nativo de Tauri.
 * @param {Blob} blob - El blob a guardar
 * @param {string} defaultFileName - Nombre sugerido para el archivo
 * @param {Object} options - Opciones adicionales (ver saveArrayBufferToDisk)
 * @returns {Promise<string|null>} - Ruta del archivo guardado o null si se canceló
 */
export async function saveBlobToDisk(blob, defaultFileName, options = {}) {
  try {
    // Convertir Blob a ArrayBuffer
    const arrayBuffer = await blob.arrayBuffer();
    return await saveArrayBufferToDisk(arrayBuffer, defaultFileName, options);
  } catch (error) {
    console.error('Error al guardar blob con Tauri:', error);
    throw error;
  }
}
